// Enhanced storage system using IndexedDB for larger capacity
class ImageStorageManager {
  constructor() {
    this.dbName = 'SaltFishyImages';
    this.dbVersion = 1;
    this.storeName = 'images';
    this.db = null;
  }

  // Initialize IndexedDB
  async init() {
    if (this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'id' });
        }
      };
    });
  }

  // Store image in IndexedDB
  async storeImage(imageData, fishId) {
    await this.init();
    
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    const imageRecord = {
      id: `fish_${fishId}_${Date.now()}`,
      fishId: fishId,
      data: imageData,
      timestamp: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(imageRecord);
      request.onsuccess = () => resolve(imageRecord.id);
      request.onerror = () => reject(request.error);
    });
  }

  // Get image from IndexedDB
  async getImage(imageId) {
    await this.init();
    
    const transaction = this.db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.get(imageId);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete image from IndexedDB
  async deleteImage(imageId) {
    await this.init();
    
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(imageId);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // Clean up old images
  async cleanup() {
    await this.init();
    
    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    
    // Delete images older than 30 days
    const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve) => {
      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.timestamp < cutoff) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve(true);
        }
      };
    });
  }

  // Get storage usage
  async getStorageInfo() {
    await this.init();
    
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      return await navigator.storage.estimate();
    }
    return { usage: 0, quota: 0 };
  }
}

// Create singleton instance
export const imageStorage = new ImageStorageManager();

// Helper function to compress image more aggressively
export const compressImage = (file, maxWidth = 300, maxHeight = 200, quality = 0.6) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      let { width, height } = img;
      
      // Calculate new dimensions
      if (width > height) {
        if (width > maxWidth) {
          height = height * (maxWidth / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = width * (maxHeight / height);
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try different compression levels if too large
      let compressedData = canvas.toDataURL('image/jpeg', quality);
      
      // If still too large, reduce quality further
      if (compressedData.length > 100000) {
        compressedData = canvas.toDataURL('image/jpeg', 0.4);
      }
      
      if (compressedData.length > 150000) {
        compressedData = canvas.toDataURL('image/jpeg', 0.3);
      }
      
      resolve(compressedData);
    };
    
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};